import React, { useEffect, useRef } from 'react';
import { CircularProgress, Box, Typography } from '@mui/material';
import { SmartToy as BotIcon, Person as UserIcon, ErrorOutline as ErrorIcon } from '@mui/icons-material'; // Icons
import { styled } from '@mui/material/styles'; // Import styled
import {
    ChatArea,
    PlaceholderBox,
    PlaceholderText,
    MessageRow,
    MessageBubble,
    Timestamp,
    BotAvatar,
    ErrorMessage,
    LoadingIndicator,
} from './ChatDisplay.styles';
import { DocPlaceholderSubstitution } from './DynamicUI/DocPlaceholderSubstitution';

const StyledCitationBubble = styled('span')(({ theme }) => ({
    display: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.light, // Or another suitable color
    color: theme.palette.primary.contrastText,
    fontSize: '0.75rem',
    fontWeight: 'bold',
    margin: '0 4px',
    cursor: 'pointer',
    verticalAlign: 'middle', // Align with text
    transition: 'background-color 0.2s ease-in-out',
    '&:hover': {
        backgroundColor: theme.palette.primary.main, // Darken on hover
    },
}));

const CitationBubble = ({ identifier, index, onClick }) => {
    const handleClick = (e) => {
        e.preventDefault();
        onClick(identifier);
    };
    return (
        <StyledCitationBubble onClick={handleClick} title={`View Citation: ${identifier}`}>
            {index + 1} {/* Display 1-based index */}
        </StyledCitationBubble>
    );
};


const formatTimestamp = (isoString) => {
    if (!isoString) return '';
    try {
        return new Date(isoString).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    } catch (e) {
        console.warn("Failed to format timestamp:", isoString, e);
        return '';
    }
};

const MessageContent = ({ message, onCitationClick }) => {
    if (message.type === 'error') {
        return <ErrorMessage><ErrorIcon fontSize="small" sx={{ verticalAlign: 'bottom', mr: 1 }} />{message.content}</ErrorMessage>;
    }
    if (message.type === 'loading') {
        return <LoadingIndicator><CircularProgress size={20} sx={{ mr: 1 }} /> Thinking...</LoadingIndicator>;
    }

    const textContent = typeof message.content === 'string' ? message.content : JSON.stringify(message.content);

    // Regex to find citations: [citation: identifier] (identifier can have spaces, dots etc.)
    // Captures everything between ": " and "]" non-greedily
    const citationRegex = /\[citation:\s*(.*?)\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let citationIndex = 0; // To number the bubbles

    while ((match = citationRegex.exec(textContent)) !== null) {
        // Add text before the citation
        if (match.index > lastIndex) {
            parts.push(textContent.substring(lastIndex, match.index));
        }

        // Extract the identifier (content between ': ' and ']')
        const identifier = match[1].trim(); // Trim whitespace

        // Log the citation value
        console.log("Found citation identifier:", identifier);

        if (identifier && onCitationClick) { // Check if identifier is not empty and handler exists
            parts.push(
                <CitationBubble
                    key={`cite-${message.id || message.timestamp}-${citationIndex}`}
                    identifier={identifier}
                    index={citationIndex}
                    onClick={onCitationClick}
                />
            );
            citationIndex++;
        } else {
            // Fallback: Render the raw citation text if no handler or empty identifier
            parts.push(match[0]);
            console.warn("Could not render citation bubble. Identifier:", identifier, "Handler exists:", !!onCitationClick);
        }
        lastIndex = citationRegex.lastIndex;
    }

    // Add any remaining text after the last citation
    if (lastIndex < textContent.length) {
        parts.push(textContent.substring(lastIndex));
    }

    const decodeUnicode = (str) =>
        str.replace(/\\u([\dA-Fa-f]{4})/g, (_, g1) => String.fromCharCode(parseInt(g1, 16)));

    const renderWithLineBreaks = (part, i) => {
        if (typeof part !== 'string') return <React.Fragment key={i}>{part}</React.Fragment>;
        const decoded = decodeUnicode(part);
        const split = decoded.split(/\\n/);
        return split.map((line, idx) =>
            idx === split.length - 1
                ? line
                : [line, <br key={`br-${i}-${idx}`} />]
        );
    };

    return (
        <>
            {/* Render the parts (strings and CitationBubble components) */}
            <Typography component="span" variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {parts.map((part, i) => (
                    <React.Fragment key={i}>{renderWithLineBreaks(part, i)}</React.Fragment>
                ))}
            </Typography>
            {message.timestamp && (
                <Timestamp messagetype={message.role || message.type}>
                    {formatTimestamp(message.timestamp)}
                </Timestamp>
            )}
        </>
    );
};

// Update ChatDisplay props definition
const ChatDisplay = ({ messages, isLoading, error, placeholderMessage = "Start generating your contract by typing below.", onCitationClick }) => {
    const chatEndRef = useRef(null);

    // Scroll to the bottom when a new message is added
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const renderMessage = (message, index) => {
        if (message.type === 'loading') {
            return <LoadingIndicator key={`loading-${index}`}><CircularProgress size={24} /></LoadingIndicator>;
        }
        if (message.type === 'error') {
            return <ErrorMessage key={`error-${index}`}><ErrorIcon fontSize="small" sx={{ verticalAlign: 'bottom', mr: 1 }} />{message.content}</ErrorMessage>;
        }
        const isUser = message.role === 'user';
        if (message.type === 'doc') {
            return (
                <MessageRow key={index} messagerole={message.role}>
                    {!isUser && <BotAvatar><BotIcon fontSize="small" /></BotAvatar>}
                    <MessageBubble messagerole={message.role}>
                        <DocPlaceholderSubstitution incomingDocument={message.document} incomingVariables={message.variables} />
                    </MessageBubble>
                </MessageRow>
            )
        }
        return (
            <MessageRow key={index} messagerole={message.role}>
                {!isUser && <BotAvatar><BotIcon fontSize="small" /></BotAvatar>}
                <MessageBubble messagerole={message.role}>
                    <MessageContent message={message} onCitationClick={onCitationClick} />
                </MessageBubble>
            </MessageRow>
        );
    };

    return (
        <ChatArea elevation={0}>
            {messages.length === 0 && !isLoading && !error && (
                <PlaceholderBox>
                    <img src="Icons/legal-demo-Logo-black.png" alt="Chat Placeholder" style={{ maxWidth: '20%', height: 'auto' }} />
                    <PlaceholderText>
                        {placeholderMessage}
                    </PlaceholderText>
                </PlaceholderBox>
            )}

            {messages.map(renderMessage)}

            <div ref={chatEndRef} />

            {error && !messages.some(m => m.type === 'error') && (
                <ErrorMessage><ErrorIcon fontSize="small" sx={{ verticalAlign: 'bottom', mr: 1 }} />{error}</ErrorMessage>
            )}
        </ChatArea>
    );
};

export default ChatDisplay;
